import { Button, Col, notification, Popconfirm, Row, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { compact, has, isEmpty, orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { styled } from 'styled-components'
import {
  Can,
  Copyable,
  Icon,
  IconContainer,
  InfoModal,
  PageContainer,
  PageTitleRow,
} from '../../components'
import { withConfig, withProfile } from '../../modules'
import { configPropTypes, userPropTypes } from '../../types'
import {
  createDownloadLink,
  handleBackendError,
} from '../../utils'
import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import { getCountryList } from '../country-management/api'
import { WithdrawalInfoModal } from './WithdrawalInfoModal'

import { cancelWithdrawal, exportWithdrawals, getBankData, getWithdrawalHistory, payoutWithdrawal } from './api'

const WithdrawalStatus = styled.div`
  text-transform: capitalize;
  color: #cc3434;
  ${(props) => props.type === 'completed' && 'color: #2fb327'};
  ${(props) => props.type === 'pending' && 'color: #36f'};
`
export const WithdrawHistory = ({ config, user }) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [isWithrawalInfoModalVisible, setWithdrawalModalVisibility] = useState(false)
  const [isBankInfoModalVisible, setBankInfoModalVisibility] = useState(false)

  const [withdrawals, setWithdrawals] = useState([])
  const [selectedWithdrawal, setSelectedWithdrawal] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [countryFilters, setCountryFilters] = useState([])

  const [metaData, setMetaData] = useState()

  const typeFilter = [
    { text: 'Paypal', value: 'PaymentMethods::Paypal' },
    { text: 'Pring', value: 'PaymentMethods::Pring' },
    { text: 'Transferwise', value: 'PaymentMethods::Transferwise' },
  ]

  const CLOSED_COUNTRIES = ['United States', 'United Kingdom', 'Canada']

  const statusFilters = orderBy(
    config.config?.withdrawal_statuses.map((status) => ({ text: status, value: status })),
    'text',
    'desc',
  )

  useEffect(() => {
    if (isEmpty(searchParams.toString()))
      setSearchParams({ items_per_page: 25, page: 1 })
  }, [])

  useEffect(() => {
    getCountryList()
      .then(({ data }) => {
        const filters = orderBy(
          data.data.map(({ name, id }) => ({ text: name, value: id })),
          'text',
          'desc',
        )

        if (filters.length > 0) {
          setCountryFilters(filters)
        }
      })
      .catch((error) => {
        handleFailure(error, 'Fetching countries failed!')
      })
  }, [])

  useEffect(() => {
    if (!isEmpty(searchParams.toString())) fetchWithdrawals(searchParams.toString())
  }, [searchParams])

  const fetchWithdrawals = (params) => {
    setIsLoading(true)

    getWithdrawalHistory(params)
      .then(({ data }) => {
        setMetaData(data.meta)
        setWithdrawals(data.data)
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)
        handleFailure(error, 'Fetching withdrawal history failed!')
      })
  }

  const userCanCheckDetails = has(config.config.user_permissions, 'withdrawal.get.withdrawal_id')
  const userCanCheckBankData = has(config.config.user_permissions, 'withdrawal.get.withdrawal_id_bank_data')

  const tableColumns = compact([
    {
      className: 'withdrawalIdSelenium',
      render: (_, record) => (
        <div style={{ maxWidth: 150 }}>
          <Copyable text={record.id} />
        </div>
      ),
      title: 'Withdrawal Id',
      ...getTableFilter({ name: 'id' }),
    },
    {
      className: 'withdrawalStatusSelenium',
      render: (_, record) => <WithdrawalStatus type={record.status}>{record.status}</WithdrawalStatus>,
      title: 'Status',
      ...getTableFilter({ filterValues: statusFilters, name: 'status[]', type: 'checkbox' }),
    },
    {
      className: 'userNameSelenium',
      dataIndex: 'name',
      title: 'User name',
      ...getTableFilter({ name: 'name' }),
    },
    {
      className: 'countrySelenium',
      render: (_, record) => <>{record.country_name}</>,
      title: 'Country',
      ...getTableFilter({ filterValues: countryFilters, isMultiple: false, name: 'country_id', type: 'checkbox' }),
    },
    {
      className: 'createdAtSelenium',
      dataIndex: 'created_at',
      render: (_, record) => <span>{dayjs(record.created_at).format('DD.MM.YYYY')}</span>,
      sorter: true,
      title: 'Created at',
      ...getTableFilter({ name: 'created_at', type: 'date' }),
    },
    {
      className: 'updatedAtSelenium',
      key: 'updated_at',
      render: (_, record) => <span>{dayjs(record.updated_at).format('DD.MM.YYYY')}</span>,
      title: 'Updated at',
    },
    {
      className: 'amountSelenium',
      dataIndex: 'amount',
      key: 'amount',
      render: (_, record) => (
        <div>
          {new Intl.NumberFormat(record.locale, { currency: record.currency_symbol, style: 'currency' }).format(
            record.amount,
          )}
        </div>
      ),
      sorter: true,
      title: 'Amount',
    },
    {
      className: 'typeSelenium',
      filteredValue: searchParams.getAll('type[]'),
      key: 'type',
      render: (_, record) => <div>{record.type.replace('PaymentMethods::', '')}</div>,
      title: 'Type',
      ...getTableFilter({ filterValues: typeFilter, name: 'type[]', type: 'checkbox' }),
    },
    userCanCheckBankData
      ? {
        className: 'toSelenium',
        key: 'to',
        render: (_, record) =>
          record.type === 'PaymentMethods::Transferwise' ? (
            <IconContainer>
              <Button
                data-testid="bankDataBtn"
                name="bankDataButtonSelenium"
                onClick={() => handleOpenBankInfoModal(record)}
                type="primary"
              >
                BANK DATA
              </Button>
            </IconContainer>
          ) : (
            <span name="accountNumberSelenium">{record.account_number}</span>
          ),
        title: 'To',
      }
      : null,
    userCanCheckDetails
      ? {
        className: 'detailsSelenium',
        key: 'details',
        render: (_, record) => (
          <Button
            data-testid="detailsButtonSelenium"
            name="detailsButtonSelenium"
            onClick={() => handleOpenWithdrawalInfoModal(record)}
            type="link"
          >
            Details
          </Button>
        ),
        title: 'Details',
      }
      : null,
    {
      className: 'withdrawActionSelenium',
      key: 'action',
      render: (_, record) => {
        const isFranceWise = record.country_name === 'France' && record.type === 'PaymentMethods::Transferwise'
        const isClosedCountry = CLOSED_COUNTRIES.includes(record.country_name)

        const adminCountries = user?.me?.countries?.map((country) => country.name) || []
        const adminInSameCountry = !isEmpty(adminCountries) && adminCountries.includes(record.country_name)

        return (
          <IconContainer>
            {record.status === 'pending' && !isFranceWise && adminInSameCountry && (
              <>
                {!isClosedCountry && (
                  <Can
                    requiredPermission="withdrawal.put.withdrawal_id_payout"
                    yes={() => (
                      <Tooltip title="Pay out">
                        <Popconfirm
                          cancelText="Cancel"
                          className="action-list-button"
                          okText="Confirm"
                          onConfirm={() => handlePayoutConfirmation(record.id)}
                          title="Are you sure you want to confirm this withdrawal payout?"
                        >
                          <Icon
                            data-testid="confirmPayoutSelenium"
                            name="confirmPayoutSelenium"
                            style={{ color: 'green' }}
                            type="checkCircle"
                          />
                        </Popconfirm>
                      </Tooltip>
                    )}
                  />
                )}
                <Can
                  requiredPermission="withdrawal.put.withdrawal_id_cancel"
                  yes={() => (
                    <Tooltip title="Decline">
                      <Popconfirm
                        cancelText="Cancel"
                        className="action-list-button"
                        okText="Confirm"
                        onConfirm={() => handleCancelWithdrawal(record)}
                        title={`Are you sure you want to confirm cancelling this ${record.type.replace('PaymentMethods::', '')} withdrawal?`}
                      >
                        <Icon
                          data-testid="cancelWithdrawalSelenium"
                          name="cancelWithdrawalSelenium"
                          style={{ color: 'red' }}
                          type="close"
                        />
                      </Popconfirm>
                    </Tooltip>
                  )}
                />
              </>
            )}
          </IconContainer>
        )
      },
      title: 'Actions',
    },
  ])

  const handleExportWithdrawalsCSV = () => {
    exportWithdrawals(searchParams.toString())
      .then(({ blob, fileName }) => {
        createDownloadLink(blob, fileName)

        notification.success({
          description: 'Withdrawals exported',
          duration: 3,
          title: 'Success',
        })
      })
      .catch((error) => {
        handleFailure(error, 'Withdrawals export failed!')
      })
  }

  const handleCancelWithdrawal = (record) =>
    cancelWithdrawal(record.id)
      .then(() =>
        handleSuccess({ description: `${record.type.replace('PaymentMethods::', '')} withdrawal cancelled` }, true),
      )
      .catch((error) =>
        handleFailure(error, `${record.type.replace('PaymentMethods::', '')} withdrawal cancellation failed!`),
      )

  const handleOpenWithdrawalInfoModal = (selectedWithdrawal) => {
    setSelectedWithdrawal(selectedWithdrawal)
    setWithdrawalModalVisibility(true)
  }

  const handleOpenBankInfoModal = (selectedWithdrawal) => {
    setSelectedWithdrawal(selectedWithdrawal)
    setBankInfoModalVisibility(true)
  }

  const handleSuccess = (messageOptions) => {
    fetchWithdrawals(searchParams.toString())

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }
    const errorMessage = handleBackendError(error, message)

    notification.error({
      description: errorMessage,
      title: 'Error',
    })
  }

  const handlePayoutConfirmation = (id) => {
    return payoutWithdrawal(id)
      .then(() => handleSuccess({ description: 'Withdrawal payout confirmed' }))
      .catch((error) => handleFailure(error, 'Withdrawal payout confirmation failed!'))
  }

  return (
    <PageContainer>
      <PageTitleRow title="Withdrawals">
        <Can
          requiredPermission="withdrawal.get.export"
          yes={() => (
            <Button
              data-testid="exportWithdrawalsSelenium"
              name="exportWithdrawalsSelenium"
              onClick={handleExportWithdrawalsCSV}
            >
              Export CSV
            </Button>
          )}
        />
      </PageTitleRow>
      <Row>
        <Col span={24}>
          <Table
            className="withdrawalHistoryPageTableSelenium"
            columns={tableColumns}
            data-testid="withdrawalHistoryPageTableSelenium"
            dataSource={withdrawals}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
            pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
            rowKey="id"
            scroll={{ x: true }}
          />

          {isWithrawalInfoModalVisible && (
            <WithdrawalInfoModal
              hideModal={() => setWithdrawalModalVisibility(false)}
              selectedWithdrawal={selectedWithdrawal}
            />
          )}

          {isBankInfoModalVisible && (
            <InfoModal
              apiRequest={getBankData}
              hideModal={() => setBankInfoModalVisibility(false)}
              selectedId={selectedWithdrawal.id}
              title="Details of TransferWise withdrawal"
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

WithdrawHistory.propTypes = {
  config: configPropTypes,
  user: userPropTypes,
}

export default withConfig(withProfile(WithdrawHistory))
